/**
 * Microphone capture and playback for the voice bridge.
 *
 * The wire format is fixed by server/icpaas-ai/voiceBridge.ts:
 *   up    PCM16 mono @ 16 kHz
 *   down  PCM16 mono @ the rate announced in the `ready` message
 *
 * Without this, VoiceManager opened a socket, sent `hello`, and then discarded
 * every audio frame it received while never sending one — so `startVoiceCall()`
 * connected but no call ever happened.
 */

export const CAPTURE_RATE = 16000;
const PROCESSOR_FRAMES = 2048;

type AudioContextCtor = typeof AudioContext;

const getAudioContext = (): AudioContextCtor => {
  const ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!ctor) throw new Error('Web Audio API is not available in this browser');
  return ctor;
};

/** Clamp and convert float samples to signed 16-bit PCM. */
export function floatTo16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Linear resample — the mic runs at the device rate, the bridge wants 16 kHz. */
export function resample(input: Int16Array, from: number, to: number): Int16Array {
  if (from === to) return input;
  const ratio = from / to;
  const len = Math.round(input.length / ratio);
  const out = new Int16Array(len);
  for (let i = 0; i < len; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    out[i] = input[i0] + (input[i1] - input[i0]) * (pos - i0);
  }
  return out;
}

/** Captures the mic and hands 16 kHz PCM16 frames to `onFrame`. */
export class MicCapture {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private analyserNode: AnalyserNode | null = null;

  get analyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  async start(onFrame: (pcm: ArrayBuffer) => void): Promise<void> {
    if (!window.isSecureContext) {
      throw new Error('Microphone access requires a secure context (https or localhost)');
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('This browser does not support microphone capture');
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

    const Ctor = getAudioContext();
    this.ctx = new Ctor();
    const source = this.ctx.createMediaStreamSource(this.stream);

    this.analyserNode = this.ctx.createAnalyser();
    this.analyserNode.fftSize = 256;
    source.connect(this.analyserNode);

    this.processor = this.ctx.createScriptProcessor(PROCESSOR_FRAMES, 1, 1);
    this.processor.onaudioprocess = (e) => {
      const pcm = resample(
        floatTo16(e.inputBuffer.getChannelData(0)),
        this.ctx!.sampleRate,
        CAPTURE_RATE,
      );
      // Copy out of the pooled buffer: the same backing store is reused next tick.
      onFrame(pcm.buffer.slice(0) as ArrayBuffer);
    };
    source.connect(this.processor);

    // A ScriptProcessor only ticks while connected to the graph; a zero-gain
    // sink keeps it running without echoing the mic back out of the speakers.
    const sink = this.ctx.createGain();
    sink.gain.value = 0;
    this.processor.connect(sink);
    sink.connect(this.ctx.destination);
  }

  stop(): void {
    try { this.processor?.disconnect(); } catch { /* not connected */ }
    try { this.stream?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    try { this.ctx?.close(); } catch { /* already closed */ }
    this.processor = null;
    this.stream = null;
    this.ctx = null;
    this.analyserNode = null;
  }
}

/** Plays the bridge's PCM16 chunks back-to-back, with barge-in support. */
export class AudioPlayback {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private sources: AudioBufferSourceNode[] = [];
  private nextStart = 0;
  private rate = 24000;

  get analyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  start(sampleRate = 24000): void {
    this.stop();
    this.rate = sampleRate || 24000;
    const Ctor = getAudioContext();
    this.ctx = new Ctor();
    this.gain = this.ctx.createGain();
    this.analyserNode = this.ctx.createAnalyser();
    this.analyserNode.fftSize = 256;
    this.gain.connect(this.analyserNode);
    this.analyserNode.connect(this.ctx.destination);
    this.nextStart = 0;
    this.sources = [];
  }

  push(chunk: ArrayBuffer): void {
    if (!this.ctx || !this.gain || this.ctx.state === 'closed') return;

    const i16 = new Int16Array(chunk);
    const f32 = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;

    const buffer = this.ctx.createBuffer(1, f32.length, this.rate);
    buffer.copyToChannel(f32, 0);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.gain);

    // Scheduled back-to-back so chunks play gaplessly instead of overlapping.
    const at = Math.max(this.nextStart, this.ctx.currentTime);
    source.start(at);
    this.nextStart = at + buffer.duration;
    this.sources.push(source);
    source.onended = () => {
      const i = this.sources.indexOf(source);
      if (i >= 0) this.sources.splice(i, 1);
    };
  }

  /** Barge-in: drop everything queued, or the caller keeps hearing the interrupted reply. */
  flush(): void {
    for (const s of this.sources) {
      try { s.stop(); } catch { /* already ended */ }
    }
    this.sources = [];
    this.nextStart = this.ctx ? this.ctx.currentTime : 0;
  }

  /** Milliseconds of audio still queued — used to let a goodbye finish. */
  remainingMs(): number {
    if (!this.ctx) return 0;
    return Math.max(0, (this.nextStart - this.ctx.currentTime) * 1000);
  }

  stop(): void {
    this.flush();
    try { this.ctx?.close(); } catch { /* already closed */ }
    this.ctx = null;
    this.gain = null;
    this.analyserNode = null;
  }
}
